import { motion } from 'motion/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { NewQuestion } from '@/types';
import styles from './QuestionListItem.module.scss';

interface QuestionListItemProps {
  question: NewQuestion;
  index: number;
  isSelected: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

export function QuestionListItem({
  question,
  index,
  isSelected,
  onEdit,
  onDelete,
}: QuestionListItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.order_index.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getQuestionTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      multiple_choice: 'MC',
      fill_blank: 'Fill Blank',
      mix_match: 'Match',
      short_answer: 'Short',
      long_answer: 'Essay',
    };
    return labels[type] || type;
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      className={`${styles.item} ${isSelected ? styles['item--selected'] : ''} ${
        isDragging ? styles['item--dragging'] : ''
      }`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isDragging ? 0.5 : 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      layout
    >
      {/* Drag Handle */}
      <div className={styles.item__dragHandle} {...attributes} {...listeners}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 5h10M3 8h10M3 11h10"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* Content */}
      <div className={styles.item__content}>
        <div className={styles.item__header}>
          <span className={styles.item__number}>{index + 1}</span>
          <span className={styles.item__type}>
            {getQuestionTypeLabel(question.question_type)}
          </span>
          <span className={styles.item__difficulty}>{question.difficulty}</span>
        </div>

        <p className={styles.item__text}>{truncateText(question.question_text, 100)}</p>
      </div>

      {/* Actions */}
      <div className={styles.item__actions}>
        <motion.button
          className={styles.item__editButton}
          onClick={onEdit}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Edit
        </motion.button>
        <motion.button
          className={styles.item__deleteButton}
          onClick={onDelete}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Delete
        </motion.button>
      </div>
    </motion.div>
  );
}
